import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import * as firebase from 'firebase/app';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class SnapBricksService {

  constructor(
    public api: ApiService,
  ) { }

  mySubscription: Subscription;
	private subscriptions: Subscription[] = []; // Arreglo para almacenar todas las suscripciones
	allBricks : any = []
  private dataSubject = new BehaviorSubject<any>(null);

  // Observable al que se pueden suscribir otras partes de la app
  data$ = this.dataSubject.asObservable();

  updateData(newData: any) {
    this.dataSubject.next(newData);
  }

  /**
   * Bricks del evento (o solo de la zona de la caja) en vivo: un producto que
   * se agrega, importa, copia o elimina desde el monitor aparece o desaparece
   * en la caja sin recargar. Antes era una lectura única, así que lo creado
   * después de abrir el evento no existía para la caja hasta volver a entrar.
   * Cada brick sigue con su propio listener de presentaciones (inventario).
   *
   * `zone_keys` (la zona de la caja con sus sub-zonas): solo esos productos
   * entran y abren su listener de presentaciones. Un evento de cinco barras
   * con categorías anidadas tiene cientos de productos; la caja de una barra
   * necesita los suyos, no un listener por cada producto del evento.
   */
  getBricks(data_event){
    this.allBricks =[]
	this.cancelSus()
    const zoneKeys: string[] | null = Array.isArray(data_event.zone_keys) && data_event.zone_keys.length > 0 ? data_event.zone_keys : null

    let query: firebase.firestore.Query = this.api.getRef("bricks").ref
      .where('event_key', '==', data_event.key)
    // solamente bricks de la zona en la que me encuentro (con zone_keys el
    // filtro es local: incluye las sub-zonas, que un where por category no ve)
    if (data_event.zone_event_get && !zoneKeys) {
      query = query.where('category', '==', data_event.default_zone)
    }

    const unsubscribe = query.onSnapshot(snapshot => {
      snapshot.docChanges().forEach(change => {
        const item: any = change.doc.data()
        // La llave viene grabada en el doc; si faltara, el id del doc.
        if (!item.$key) item.$key = change.doc.id
        // `visible` del doc se guarda aparte: el listener de presentaciones
        // pisa `visible` con "hay stock", y para saber si está oculto desde
        // el monitor hace falta el valor original.
        item.doc_visible = item.visible
        // solo productos visibles para la caja: tipo brick y no eliminados desde monitor
        const inZone = !zoneKeys || zoneKeys.indexOf(item.category) >= 0
        const alive = inZone && item.type == "brick" && (item.delete === false || item.delete === undefined)
        const tracked = this.allBricks.find(b => b.$key === item.$key)

        if (change.type === "removed" || !alive) {
          if (tracked || this.presentationSubs[item.$key]) this.dropBrick(item.$key)
          return
        }
        if (!tracked) {
          if (!this.presentationSubs[item.$key]) this.getPresentationSnap(item)
          return
        }
        // Cambió el doc (nombre, precio, imagen, visible…): se conserva lo que
        // ya trae el listener de presentaciones y se recalcula si está oculto.
        Object.assign(tracked, item, { presentation: tracked.presentation, visible: tracked.visible, doc_visible: item.visible })
        tracked.hidden = SnapBricksService.isHidden(tracked, tracked.presentation)
        this.updateData(this.allBricks)
      })
    }, error => {
      console.error('❌ Error escuchando los bricks del evento:', error)
    })

    this.subscriptions.push(new Subscription(() => unsubscribe()))
  }

  /**
   * Oculto = el monitor lo apagó: `visible: false` en el documento del
   * producto (guardado en `doc_visible` al leerlo) o en la presentación que
   * vende la caja.
   */
  static isHidden(brick: any, presentation: any): boolean {
    const docHidden = !!brick && brick.doc_visible === false;
    const presentationHidden = !!presentation && presentation.visible === false;
    return docHidden || presentationHidden;
  }

  /** Listener de presentaciones por brick, para poder soltarlo si el brick se elimina. */
  private presentationSubs: { [brickKey: string]: Subscription } = {}

  private dropBrick(brickKey: string) {
    const sub = this.presentationSubs[brickKey]
    if (sub) {
      sub.unsubscribe()
      delete this.presentationSubs[brickKey]
      this.subscriptions = this.subscriptions.filter(s => s !== sub)
    }
    const index = this.allBricks.findIndex(b => b.$key === brickKey)
    if (index !== -1) {
      this.allBricks.splice(index, 1)
      this.updateData(this.allBricks)
    }
  }
  
  async getPresentationSnap(brick) {

	// Conjunto para evitar duplicados en "added"
	const processedDocs = new Set();
  
	this.mySubscription = await this.getSnapshotChanges(brick.$key)
	  .subscribe(change => {
		const docId = change.doc.id;
		const presentation = change.doc.data();
		presentation.brick_key = brick.$key;
		if (!presentation.$key) presentation.$key = docId;
  
		if (change.type === "added") {
		  // Si el documento ya fue procesado en "added", lo ignoramos
		  if (processedDocs.has(docId)) return;
		  processedDocs.add(docId);
  
		  brick.presentation = presentation;
		  // <= 0 y no === 0: con el descuento atómico de inventario, dos cajas
		  // vendiendo lo mismo a la vez (o una sin conexión) pueden dejarlo en
		  // negativo, y eso también es "sin existencias".
		  brick.visible = (presentation.visible === false || presentation.inventory <= 0) ? false : true;
		  // Oculto desde el monitor (el doc del producto o su presentación con
		  // visible: false): la caja no lo ofrece. Distinto de "sin stock".
		  brick.hidden = SnapBricksService.isHidden(brick, presentation);
  
		  // Verifica si el elemento ya está en el array antes de agregarlo
		  if (this.allBricks.findIndex(item => item.$key === brick.$key) === -1) {
			this.allBricks.push(brick);
		  }
		} 
  
		else if (change.type === "modified") {
		  // Busca el índice del elemento a modificar
		  const index = this.allBricks.findIndex(item => item.$key === brick.$key);
		  if (index !== -1) {
			this.allBricks[index] = { 
			  ...this.allBricks[index], 
			  presentation: presentation,
			  visible: (presentation.visible === false || presentation.inventory <= 0) ? false : true,
			  hidden: SnapBricksService.isHidden(this.allBricks[index], presentation),
			};
		  }
		}
  
		this.updateData(this.allBricks);
	  }, error => {
		console.error('❌ Error in snapshot listener:', error);
	  });
  
	this.subscriptions.push(this.mySubscription);
	this.presentationSubs[brick.$key] = this.mySubscription;
  }
  
  

  getSnapshotChanges(brickKey: string): Observable<any> {
		return new Observable(observer => {
		  const unsubscribe = this.api.getRef(`bricks/${brickKey}/presentations`).ref
			.onSnapshot(snapshot => {
			  // Emitir cada cambio al observer
			  snapshot.docChanges().forEach(change => {
				observer.next(change);
			  });
			}, 
			error => {
			  // Emitir error en caso de que algo salga mal
			  observer.error(error);
			});
	  
		  // Retorna la función de cleanup para desuscribirse
		  return () => unsubscribe();
		});
	  }

    private suscriptionPresentations: Subscription = null

  cancelSus(){
		console.error("delete suscrip == ");
    console.log(this.subscriptions);
    
		if (this.subscriptions.length > 0) this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = []
    this.presentationSubs = {}
	}

}
